import { useEffect, useState } from "react";
import { api, formatDMY } from "@/lib/api";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Trash2, Mail, Phone } from "lucide-react";

const STATUSES = ["New", "Contacted", "Closed"];

export default function EnquiriesTab({ onChange }) {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = filter !== "all" ? { status: filter } : {};
      const { data } = await api.get("/inquiries", { params });
      setRows(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [filter]);

  const updateStatus = async (id, status) => {
    try {
      await api.put(`/inquiries/${id}`, { status });
      toast.success("Status updated");
      load();
      onChange?.();
    } catch {
      toast.error("Failed to update");
    }
  };

  const updateNotes = async (id, admin_notes) => {
    try {
      await api.put(`/inquiries/${id}`, { admin_notes });
      toast.success("Notes saved");
      load();
    } catch {
      toast.error("Failed to save");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this inquiry?")) return;
    try {
      await api.delete(`/inquiries/${id}`);
      toast.success("Deleted");
      load();
      onChange?.();
    } catch {
      toast.error("Delete failed");
    }
  };

  const badge = (s) => {
    if (s === "Closed") return "bg-[#00CC66] text-white";
    if (s === "Contacted") return "bg-[#FF9900] text-black";
    return "bg-[#FF4D4D] text-white";
  };

  return (
    <div data-testid="enquiries-tab">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6">
        <h3 className="font-heading font-black text-2xl tracking-tighter">Enquiries</h3>
        <div className="flex flex-wrap gap-2">
          {["all", ...STATUSES].map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              data-testid={`enquiry-filter-${s.toLowerCase()}`}
              className={`px-4 py-2 font-bold text-sm border-2 border-[#0A0A0A] rounded-full transition-transform ${
                filter === s ? "bg-[#0A0A0A] text-[#FFE600] shadow-brutal-sm" : "bg-white hover:shadow-brutal-sm"
              }`}
            >
              {s === "all" ? "All" : s}
            </button>
          ))}
        </div>
      </div>

      {loading && <div className="brutal-card p-8 text-center font-medium text-[#4A4A4A]">Loading…</div>}
      {!loading && rows.length === 0 && (
        <div className="brutal-card p-8 text-center font-medium text-[#4A4A4A]">
          No enquiries. Public messages will show up here.
        </div>
      )}

      <div className="space-y-4">
        {rows.map((q) => (
          <div key={q.id} className="brutal-card p-5 bg-white" data-testid={`enquiry-${q.id}`}>
            <div className="flex items-start justify-between flex-wrap gap-3">
              <div className="min-w-0">
                <h4 className="font-heading font-black text-xl">{q.name}</h4>
                <div className="mt-1 flex items-center gap-3 text-sm font-medium text-[#4A4A4A] flex-wrap">
                  <a href={`tel:${q.contact}`} className="flex items-center gap-1 font-bold hover:underline">
                    <Phone size={12} /> {q.contact}
                  </a>
                  {q.email && (
                    <a href={`mailto:${q.email}`} className="flex items-center gap-1 font-bold hover:underline">
                      <Mail size={12} /> {q.email}
                    </a>
                  )}
                  <span className="text-xs">{formatDMY(q.created_at.slice(0, 10))}</span>
                </div>
              </div>
              <span className={`px-3 py-1 border-2 border-[#0A0A0A] rounded-full text-xs font-bold ${badge(q.status)}`}>
                {q.status}
              </span>
            </div>
            <p className="mt-3 font-medium whitespace-pre-line">{q.message}</p>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <div className="text-xs font-bold uppercase tracking-widest mb-1.5">Status</div>
                <Select value={q.status} onValueChange={(v) => updateStatus(q.id, v)}>
                  <SelectTrigger
                    data-testid={`enquiry-status-${q.id}`}
                    className="border-2 border-[#0A0A0A] rounded-xl bg-white font-bold"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="border-2 border-[#0A0A0A]">
                    {STATUSES.map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <div className="text-xs font-bold uppercase tracking-widest mb-1.5">Admin notes</div>
                <NotesInput initial={q.admin_notes || ""} onSave={(v) => updateNotes(q.id, v)} testId={`enquiry-notes-${q.id}`} />
              </div>
            </div>
            <div className="mt-3 flex justify-end">
              <button
                onClick={() => remove(q.id)}
                data-testid={`enquiry-delete-${q.id}`}
                className="inline-flex items-center gap-2 font-bold text-xs px-3 py-1.5 border-2 border-[#0A0A0A] rounded-full bg-[#FF4D4D] text-white hover:shadow-brutal-sm transition-shadow"
              >
                <Trash2 size={12} /> Delete
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function NotesInput({ initial, onSave, testId }) {
  const [val, setVal] = useState(initial);
  useEffect(() => setVal(initial), [initial]);
  return (
    <div className="flex gap-2">
      <input
        data-testid={testId}
        value={val}
        onChange={(e) => setVal(e.target.value)}
        placeholder="Follow-up notes..."
        className="flex-1 px-3 py-2 border-2 border-[#0A0A0A] rounded-xl bg-white font-medium"
      />
      <button
        onClick={() => onSave(val)}
        className="px-4 py-2 font-bold border-2 border-[#0A0A0A] rounded-full bg-[#FFE600] hover:shadow-brutal-sm transition-shadow"
      >
        Save
      </button>
    </div>
  );
}
